import { disc, hash, paintBands, px, type GroundLook } from './art';

/**
 * Earth at night, for the tutorial (Dad's back garden): a navy sky with the Moon in it (the
 * first stop), a city skyline, the houses of the street, hedges and a street lamp passing in
 * front. Every scrolling layer tiles horizontally.
 */

type Ctx = CanvasRenderingContext2D;

export const EARTH_COLORS = {
  skyBands: ['#080a20', '#0c1030', '#11163e', '#161e4a', '#1d2654', '#262e5a', '#33355c'],
  star: '#fff6d8',
  starDim: '#6f76a8',
  moon: '#e8e4d4',
  moonShade: '#b4ae9c',
  haze: '#28305a',

  city: '#121634',
  cityLit: '#1c2244',
  window: '#e0c060',
  windowDim: '#8a7440',
  beacon: '#ff4a4a',

  house: '#0d1026',
  roof: '#090b1c',
  houseWindow: '#f0b050',
  tree: '#0a1420',
  treeLit: '#122030',

  fg: '#04060c',
  fgLit: '#0c1220',
  lamp: '#ffe6a0',
  lampGlow: '#3a3420',
} as const;

const C = EARTH_COLORS;

export const EARTH_GROUND: GroundLook = {
  cap: 'grass',
  plank: 'wood',
  colors: {
    top: '#3c8a48',
    topLit: '#6cc06a',
    topDark: '#2a643a',
    body: '#4a3428',
    bodyDark: '#34241c',
    speck: '#6a4c3c',
    ink: '#0c0a0a',
    plank: '#7a5a3a',
    plankLit: '#a87e52',
    plankDark: '#4a3420',
    hang: null,
  },
};

/** Night sky in stepped bands, stars, and the Moon with its seas. */
export function paintEarthSky(ctx: Ctx, width: number, height: number): void {
  paintBands(ctx, C.skyBands, width, height);
  for (let i = 0; i < 70; i++) {
    const x = Math.floor(hash(i, 1, 71) * width);
    const y = Math.floor(hash(i, 2, 71) * height * 0.5);
    px(ctx, hash(i, 3, 71) < 0.5 ? C.starDim : C.star, x, y);
  }
  // The Moon: the first stop. Placed to show on the narrowest (480 px) screen, below the
  // tutorial's tips.
  const mx = Math.min(width - 70, 372);
  const my = 96;
  disc(ctx, mx, my, 17, C.moon);
  for (const [dx, dy, r] of [
    [-5, -4, 4],
    [4, 3, 5],
    [-3, 7, 3],
    [6, -7, 2],
  ] as const) {
    disc(ctx, mx + dx, my + dy, r, C.moonShade);
  }
}

/** The city on the horizon: towers with a few lit windows and a radio mast's red light. */
export function paintEarthCity(ctx: Ctx, width: number, height: number): void {
  let x = 0;
  let i = 0;
  while (x < width) {
    const w = 14 + Math.floor(hash(i, 1, 73) * 22);
    const top = 150 + Math.floor(hash(i, 2, 73) * 50);
    const right = Math.min(width, x + w);
    px(ctx, i % 2 === 0 ? C.city : C.cityLit, x, top, right - x, height - top);
    for (let wy = top + 4; wy < height - 60; wy += 5) {
      for (let wx = x + 3; wx < right - 3; wx += 4) {
        const r = hash(wx, wy, 74);
        if (r < 0.12) px(ctx, C.window, wx, wy, 2, 2);
        else if (r < 0.2) px(ctx, C.windowDim, wx, wy, 2, 2);
      }
    }
    x = right + 2 + Math.floor(hash(i, 3, 73) * 6);
    i++;
  }
  // A radio mast with its warning light.
  px(ctx, C.city, 300, 110, 2, 60);
  px(ctx, C.beacon, 300, 108, 2, 2);
  px(ctx, C.city, 0, height - 64, width, 64);
}

/** The houses of the street: walls, pitched roofs, warm windows, round trees between them. */
export function paintEarthStreet(ctx: Ctx, width: number, height: number): void {
  const ground = height - 44;
  const houses = [
    { x: 20, w: 70, h: 44 },
    { x: 140, w: 56, h: 38 },
    { x: 260, w: 80, h: 50 },
  ];
  for (const h of houses) {
    const top = ground - h.h;
    px(ctx, C.house, h.x, top, h.w, h.h + 44);
    // Pitched roof.
    const peak = Math.floor(h.w / 2) + 4;
    for (let k = 0; k < peak; k++) {
      px(ctx, C.roof, h.x - 4 + k, top - Math.round(k / 1.6), h.w + 8 - k * 2, 1);
    }
    for (let k = 0; k < 2; k++) {
      const wx = h.x + 10 + k * (h.w - 30);
      px(ctx, C.houseWindow, wx, top + 12, 10, 8);
      px(ctx, C.house, wx + 4, top + 12, 2, 8);
    }
  }
  for (const tx of [110, 226, 362]) {
    px(ctx, C.tree, tx - 1, ground - 30, 3, 34);
    disc(ctx, tx, ground - 36, 14, C.tree);
    disc(ctx, tx - 3, ground - 39, 8, C.treeLit);
  }
  px(ctx, C.house, 0, ground, width, 44);
}

/** Hedges along the bottom and a street lamp with a warm bulb, in front of everything. */
export function paintEarthForeground(ctx: Ctx, width: number, height: number): void {
  for (const [cx, r] of [
    [Math.round(width * 0.1), 20],
    [Math.round(width * 0.16), 14],
    [Math.round(width * 0.47), 26],
    [Math.round(width * 0.9), 16],
  ] as const) {
    disc(ctx, cx, height + 4, r, C.fg);
    px(ctx, C.fgLit, cx - Math.floor(r / 2), height + 4 - r + 2, r, 1);
  }
  // The lamp: a post, an arm, a hood, a bulb and a faint glow under it.
  const lx = Math.round(width * 0.72);
  disc(ctx, lx + 16, height - 132, 5, C.lampGlow);
  px(ctx, C.fg, lx, height - 140, 3, 140);
  px(ctx, C.fg, lx, height - 140, 16, 2);
  px(ctx, C.fg, lx + 12, height - 140, 8, 4);
  px(ctx, C.lamp, lx + 14, height - 136, 4, 2);
}
